import { Component, HasView, Q } from '@fullstory/tomato';

// Interface describing a showable/hidable "pane". Used in the main navigation and
// for dialog boxes.
export interface Pane extends HasView<Q.OfOne> {
  show(): void;
  hide(done?: () => void): void; // Optional done callback in the case of animations where hiding isn't synchronous.
}

enum ShowHideState {
  // Final states.
  HIDDEN,
  SHOWN,

  // Animating.
  SHOWING,
  HIDING
}

// Normally a Pane can just hide/show. AsyncPanes split the work of showing in to two parts.
// 1)show (slide in, make UI visible) and 2)finishShow (network requests, rendering, etc... sometime later).
export interface AsyncPane extends Pane {
  finishShow(): void;
}

// Abstract base class for any panel that is hidden/shown and needs to track and maintain that state.
// Contains convenience functions that must be overloaded, that can be used for animations.
export class ShowHidePanel<V extends Q.OfOne> extends Component<V> implements Pane {
  private _showHideState = ShowHideState.HIDDEN;
  private _isHidable = true;

  private _animationDuration: number = 0;

  constructor(view: V) {
    super(view);
  }

  animationDuration(): number {
    return this._animationDuration;
  }

  setAnimationDuration(duration: number) {
    this._animationDuration = duration;
  }

  // Either shown already, or in the process of showing.
  isShowing(): boolean {
    return this._showHideState == ShowHideState.SHOWING || this._showHideState == ShowHideState.SHOWN;
  }

  // Are we in the process of hiding. But not yet hidden.
  isHiding(): boolean {
    return this._showHideState == ShowHideState.HIDING;
  }

  isHidden(): boolean {
    return this._showHideState == ShowHideState.HIDDEN;
  }

  setHidable(hidable: boolean) {
    this._isHidable = hidable;
  }

  toggle() {
    if (this._shouldHide()) {
      this.hide();
    } else {
      this.show();
    }
  }

  //////////////////////////////////////////
  // VIRTUAL. Implementors MUST override.
  //////////////////////////////////////////

  // Set your styles to show your component.
  showImpl() {
    throw ("ShowHidePanel.doShow() is Virtual. You must override.")
  }

  // Set your styles to hide your component. If you need to do anything that requires async, put it in
  // afterHide().
  hideImpl() {
    throw ("ShowHidePanel.hideImpl() is Virtual. You must override.")
  }

  // Called after hiding. Will be called after the animation timeout if there is a non-zero animation duration.
  // If there is no animation duration this will be called synchronously immediately after doHide().
  afterHide() {
    throw ("ShowHidePanel.afterHide() is Virtual. You must override.")
  }

  // Called right after showing (asynchronously). We don't wait for the animation timeout, this is just used to apply.
  showAsync() {
    throw ("ShowHidePanel.showAsync() is Virtual. You must override.")
  }

  ////////////////////////////////////////////////////////
  // WARNING: Implementors MUST NOT override these.
  // Doing so will cause them to lose the controller logic.
  ////////////////////////////////////////////////////////
  show() {
    if (this._shouldShow()) {
      this.showImpl();
      this._showHideState = ShowHideState.SHOWING;
      setTimeout(() => {
        this.showAsync();
        this._showHideState = ShowHideState.SHOWN;
      }, 20); // Very short. Async enough to allow animations to apply.
    }
  }

  hide(done?: () => void) {
    if (this._shouldHide()) {
      this._showHideState = ShowHideState.HIDING;
      this.hideImpl();
      if (this._hasAnimation()) {
        setTimeout(() => {
          this._showHideState = ShowHideState.HIDDEN;
          this.afterHide();
          if (done) { done() }
        }, this.animationDuration());
      } else {
        this._showHideState = ShowHideState.HIDDEN;
        this.afterHide();
        if (done) { done() }
      }
    }
  }

  // Private
  private _hasAnimation(): boolean {
    return this._animationDuration > 0;
  }

  private _shouldShow(): boolean {
    return !this.isShowing();
  }

  private _shouldHide(): boolean {
    return this.isShowing() && this._isHidable;
  }
}

// A Component that acts as a pane implementation, and which can contain child panes.
export abstract class ContainerPane<V extends Q.OfOne, P extends Pane = Pane> extends Component<V> implements Pane {
  private _curPane: P | undefined;

  constructor(view: V) {
    super(view);
  }

  show() {
    if (this._curPane) {
      this._curPane.show();
    }
  }

  hide(done?: () => void) {
    if (this._curPane) {
      this._curPane.hide(done);
    }
  }

  curPane(): P | undefined {
    return this._curPane;
  }

  showPane(pane: P) {
    if (pane == this._curPane) {
      return;
    }

    if (this._curPane) {
      this._curPane.hide();
      this._curPane.view().remove();
    }
    this._curPane = pane;
    this._curPane.view().appendTo(this.container());
    this._curPane.show();
  }

  abstract container(): Q.OfOne
}
